// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useEffect, useMemo } from 'react';
import { useShellStore } from '../../stores/shellStore';
import { useActiveSite } from '../../hooks/useActiveSite';
import { useDigitalTwin } from '../../hooks/useDigitalTwin';
import { buildFlowAdjacency } from '../../utils/flowRelationships';
import { deriveAssetLiveState } from '../../utils/assetLiveState';
import { useAssetPanelResize } from '../../hooks/useAssetPanelResize';
import { AssetDetailPanel } from './asset-detail/AssetDetailPanel';

/**
 * Shell-level host for the asset detail panel.
 *
 * The panel lives here rather than inside the Power Flow page so it survives
 * route navigation: selection is held in `shellStore` as an id, and this host
 * renders alongside `AskHeliosPanel` in `AppShell`, above the router. Leaving
 * the diagram therefore keeps the panel on screen.
 *
 * Per ADR-0008 Decision 2 the store holds only the id; the entity and its graph
 * context are re-derived from the digital-twin query cache. Two observers of the
 * same site dedupe onto one request, so this costs no extra network traffic —
 * and `useTwinPrefetch` has usually warmed the cache already.
 */
export default function AssetDetailPanelHost() {
  const selectedId = useShellStore(s => s.selectedAssetId);
  const panelSiteId = useShellStore(s => s.selectedAssetSiteId);
  const clearSelectedAsset = useShellStore(s => s.clearSelectedAsset);

  useEffect(() => {
    if (selectedId && !panelSiteId) clearSelectedAsset();
  }, [selectedId, panelSiteId, clearSelectedAsset]);

  if (!selectedId || !panelSiteId) return null;

  return <PanelContent selectedId={selectedId} panelSiteId={panelSiteId} />;
}

interface PanelContentProps {
  selectedId: string;
  /** Never null — the host guards on it, so the twin query is always enabled. */
  panelSiteId: string;
}

function PanelContent({ selectedId, panelSiteId }: PanelContentProps) {
  const selectAsset = useShellStore(s => s.selectAsset);
  const clearSelectedAsset = useShellStore(s => s.clearSelectedAsset);
  const askHeliosOpen = useShellStore(s => s.askHeliosOpen);
  const { handleProps } = useAssetPanelResize();
  const { siteId: activeSiteId } = useActiveSite();

  const { entities, relationships } = useDigitalTwin(panelSiteId);

  useEffect(() => {
    if (activeSiteId && panelSiteId !== activeSiteId) clearSelectedAsset();
  }, [panelSiteId, activeSiteId, clearSelectedAsset]);

  const adjacency = useMemo(
    () => buildFlowAdjacency(relationships).get(selectedId),
    [relationships, selectedId]
  );

  const live = useMemo(
    () => deriveAssetLiveState(entities, relationships, [selectedId]),
    [entities, relationships, selectedId]
  );

  const entitiesById = useMemo(() => new Map(entities.map(e => [e.id, e])), [entities]);

  return (
    <div className={`kg-panel-host${askHeliosOpen ? ' kg-panel-host--ask-helios-open' : ''}`}>
      <div className="kg-panel-resize" {...handleProps} />
      <AssetDetailPanel
        selectedId={selectedId}
        adjacency={adjacency}
        live={live}
        entitiesById={entitiesById}
        siteId={panelSiteId}
        onClose={clearSelectedAsset}
        onSelect={id => selectAsset(id, panelSiteId)}
      />
    </div>
  );
}
